import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';

/**
 * Shared layout for auth pages (Login, Signup, ForgotPassword, ResetPassword, etc.)
 * Renders the hero background image (theme-aware) under a centered modal-style card.
 * Card itself adapts to dark/light theme.
 */
// text-base (16px) on mobile is critical: iOS Safari auto-zooms when focusing inputs
// with font-size < 16px, and the zoom persists after the user navigates away (e.g. after login).
// text-sm (14px) restored on md+ to keep the desktop UI compact.
export const AUTH_INPUT_CLASS =
    'w-full px-4 py-3.5 bg-gray-50 dark:bg-white/[0.04] border border-gray-300 dark:border-white/10 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-white/30 text-base md:text-sm focus:outline-none focus:ring-2 focus:ring-[#0061FE]/40 focus:border-[#0061FE]/60 transition';

export const AUTH_BUTTON_CLASS =
    "w-full inline-flex items-center justify-center bg-[#0061FE] hover:bg-[#0052db] text-white font-['Manrope',sans-serif] font-semibold text-sm px-6 py-3.5 rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition shadow-lg shadow-[#0061FE]/30 active:scale-[0.98]";

export default function AuthLayout({
    kicker,
    title,
    subtitle,
    children,
    footer,
    maxWidth = 'max-w-md',
    showClose = true,
}) {
    const { theme } = useTheme();
    const themeStr = theme === 'dark' ? 'dark' : 'light';
    const heroBg = `/img/landing/hero_background_${themeStr}.webp`;
    const navigate = useNavigate();

    return (
        <main className="min-h-screen relative overflow-hidden">
            {/* Theme-aware hero background image, behind everything */}
            <img
                key={heroBg}
                src={heroBg}
                alt=""
                aria-hidden
                className="absolute inset-0 w-full h-full object-cover object-top"
            />
            {/* Soft overlay so the card pops out without hiding the sky completely */}
            <div className="absolute inset-0 bg-black/40 dark:bg-black/55" />

            {/* Centered card */}
            <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-20">
                <div className={`${maxWidth} w-full`}>
                    <div className="relative bg-white dark:bg-[#0f0f0f]/95 dark:backdrop-blur-md border border-gray-200 dark:border-white/10 rounded-3xl p-8 md:p-10 shadow-2xl shadow-black/40">
                        {/* Close button → back to home */}
                        {showClose && (
                            <button
                                type="button"
                                onClick={() => navigate('/')}
                                className="absolute top-4 right-4 p-1.5 rounded-full text-gray-400 dark:text-white/40 hover:text-gray-700 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition"
                                aria-label="Fermer"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        )}

                        {/* Header */}
                        {(kicker || title || subtitle) && (
                            <div className="text-center mb-8">
                                {kicker && (
                                    <p className="font-['Rethink_Sans',sans-serif] font-bold text-[12px] tracking-[3px] text-[#0061FE] dark:text-[#71a7ff] mb-3">
                                        {kicker}
                                    </p>
                                )}
                                {title && (
                                    <h1 className="font-['Rethink_Sans',sans-serif] font-normal text-gray-900 dark:text-white text-[34px] leading-[36px] tracking-[-1px] mb-3">
                                        {title}
                                    </h1>
                                )}
                                {subtitle && (
                                    <p className="font-['Rethink_Sans',sans-serif] text-gray-500 dark:text-[#a9b1bd] text-sm">
                                        {subtitle}
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Body content */}
                        {children}

                        {/* Footer links */}
                        {footer && (
                            <div className="mt-7 pt-6 border-t border-gray-200 dark:border-white/[0.06] text-center space-y-3">
                                {footer}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </main>
    );
}
